import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { WheelsApiService } from '../services/api/wheels-api.service';
import { NotificationService } from '../services/notification.service';
import {
  disableWheelReminder,
  disableWheelReminderFailure,
  disableWheelReminderSuccess,
  upsertWheelReminder,
  upsertWheelReminderFailure,
  upsertWheelReminderSuccess,
} from './reminder.actions';

@Injectable()
export class ReminderEffects {
  private readonly actions$ = inject(Actions);
  private readonly wheelsApi = inject(WheelsApiService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);

  public readonly upsert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(upsertWheelReminder),
      exhaustMap(({ wheelId, reminder, isUpdate }) =>
        this.wheelsApi.upsertReminder(wheelId, reminder).pipe(
          map((savedReminder) => {
            this.notification.showSuccess(this.translate.instant(isUpdate ? 'reminderUpdatedSuccess' : 'reminderSetSuccess'));
            return upsertWheelReminderSuccess({ wheelId, reminder: savedReminder, isUpdate });
          }),
          catchError((error: { message?: string }) => {
            const message = error.message ?? 'Could not save reminder';
            this.notification.showError(message);
            return of(upsertWheelReminderFailure({ error: message }));
          })
        )
      )
    )
  );

  public readonly disable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(disableWheelReminder),
      exhaustMap(({ wheelId }) =>
        this.wheelsApi.disableReminder(wheelId).pipe(
          map(() => {
            this.notification.showSuccess(this.translate.instant('reminderTurnedOffSuccess'));
            return disableWheelReminderSuccess({ wheelId });
          }),
          catchError((error: { message?: string }) => {
            const message = error.message ?? 'Could not turn off reminder';
            this.notification.showError(message);
            return of(disableWheelReminderFailure({ error: message }));
          })
        )
      )
    )
  );
}
